import { queryOptions } from '@tanstack/react-query';
import { useSuspenseQuery } from '@tanstack/react-query';
import type { AgencySession, Session } from '@aimmo/contracts';
import { api, assertOk, setCsrfToken } from './api.js';

/**
 * The SPA's first call. Everything downstream — routing guards, the user menu,
 * ability checks — reads from this one query.
 */
export const sessionQuery = queryOptions({
  queryKey: ['session'],
  queryFn: async (): Promise<Session> => {
    const result = await api.auth.session();
    const ok = assertOk(result);
    setCsrfToken(ok.body.csrfToken);
    return ok.body;
  },
  staleTime: 5 * 60 * 1000,
  retry: false,
});

/**
 * The session, narrowed to an agency one.
 *
 * Every domain screen is behind a route guard that redirects a platform session
 * away (M12), so reaching one here means a routing bug rather than a case to
 * render. Throwing says so instead of quietly rendering an empty shell.
 */
export function useAgencySession(): AgencySession {
  const { data: session } = useSuspenseQuery(sessionQuery);
  if (session.platform) {
    throw new Error('A platform session reached an agency screen');
  }
  return session;
}

export async function logout(): Promise<void> {
  const result = await api.auth.logout({ body: {} });
  const ok = assertOk(result);
  setCsrfToken(null);
  window.location.href = ok.body.logoutUrl ?? '/';
}
